import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Settings, Save, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateImageDescription } from '../../lib/openai';

export default function AdminSettings() {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'ai-settings'));
        if (settingsDoc.exists()) {
          setPrompt(settingsDoc.data().descriptionPrompt || '');
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        toast.error('Error al cargar la configuración');
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await setDoc(doc(db, 'settings', 'ai-settings'), {
        descriptionPrompt: prompt
      });
      toast.success('Configuración guardada');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    setTestError(null);

    try {
      // Using a test image from Unsplash that's guaranteed to be accessible
      const testImageUrl = 'https://images.unsplash.com/photo-1606918801925-e2c914c4b503';
      
      const description = await generateImageDescription(testImageUrl, prompt);
      if (description) {
        setTestResult(description);
        toast.success('Prueba exitosa');
      }
    } catch (error: any) {
      console.error('Error en prueba:', error);
      setTestError(error.message);
      toast.error('Error al realizar la prueba: ' + error.message);
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">
          Configuración de IA
        </h2>
        <Settings className="h-6 w-6 text-gray-400" />
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt para Descripción de Imágenes
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Describe este árbol de Navidad en detalle, mencionando su decoración, estilo y elementos destacados..."
              required
            />
            <p className="text-sm text-gray-500 mt-2">
              Este prompt se usará para generar descripciones automáticas de los árboles subidos.
              Usa un lenguaje claro y específico para obtener mejores resultados.
            </p>
          </div>

          {testError && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-700">{testError}</p>
              </div>
            </div>
          )}

          {testResult && (
            <div className="bg-green-50 border border-green-100 p-4 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Resultado de la prueba:</h4>
              <p className="text-sm text-green-700">{testResult}</p>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleTest}
              disabled={isTesting || !prompt}
              className="bg-secondary hover:bg-secondary-dark text-white px-6 py-2 rounded-lg flex items-center disabled:opacity-50"
            >
              {isTesting ? 'Probando...' : 'Probar Configuración'}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg flex items-center disabled:opacity-50"
            >
              <Save className="h-5 w-5 mr-2" />
              {isSaving ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">
          Ejemplo de Prompt Recomendado
        </h3>
        <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
          <p>
            Analiza este árbol de Navidad y proporciona una descripción detallada y atractiva. 
            Incluye información sobre:
            - El estilo general y tema de la decoración
            - Los colores predominantes
            - Tipos de adornos utilizados
            - Iluminación y su efecto
            - Elementos únicos o destacados
            - La impresión general que transmite
            Usa un tono positivo y festivo, manteniendo la descripción concisa pero informativa.
          </p>
        </div>
      </div>
    </div>
  );
}